import React, { useMemo } from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'

import { styles } from './loading.styles'
import { LOADING_TEST_ID } from './loading.constants'
import { HapanaLoader } from './variants/hapana/hapana-loader.component'

// Modern ES6 imports - much cleaner!
let LoaderManager = null

/**
 * Inline Themed Loader Components
 *
 * These are simplified versions of the themed loaders that can be used
 * directly in the JavaScript loading component.
 */

// Christmas Loader Component - Enhanced with dramatic animations
const ChristmasLoader = ({ size = 40, label }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: size * 2,
          height: size * 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Festive background glow */}
        <Box
          sx={{
            position: 'absolute',
            width: '120%',
            height: '120%',
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(220, 38, 38, 0.1) 0%, rgba(22, 163, 74, 0.1) 50%, rgba(245, 158, 11, 0.1) 100%)',
            animation: 'festiveBgGlow 3s ease-in-out infinite',
            '@keyframes festiveBgGlow': {
              '0%, 100%': { opacity: 0.3, transform: 'scale(1)' },
              '50%': { opacity: 0.7, transform: 'scale(1.2)' },
            },
          }}
        />

        {/* Falling snow particles */}
        {[...Array(6)].map((_, index) => (
          <Box
            key={`snow-${index}`}
            sx={{
              position: 'absolute',
              width: '4px',
              height: '4px',
              backgroundColor: '#FFFFFF',
              borderRadius: '50%',
              top: `${Math.random() * 20}%`,
              left: `${20 + Math.random() * 60}%`,
              animation: `fallingSnow${index} ${2 + Math.random() * 2}s linear infinite`,
              [`@keyframes fallingSnow${index}`]: {
                '0%': {
                  transform: 'translateY(-100px) rotate(0deg)',
                  opacity: 0,
                },
                '10%': { opacity: 1 },
                '90%': { opacity: 1 },
                '100%': {
                  transform: 'translateY(120px) rotate(360deg)',
                  opacity: 0,
                },
              },
            }}
          />
        ))}

        {/* Christmas Tree made of animated ornaments */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            '& > div': {
              position: 'absolute',
              borderRadius: '50%',
              border: '2px solid rgba(255,255,255,0.3)',
              animation: 'dramaticChristmasGlow 1.5s ease-in-out infinite alternate',
            },
            '@keyframes dramaticChristmasGlow': {
              '0%': {
                opacity: 0.4,
                transform: 'scale(0.8)',
                boxShadow: '0 0 5px rgba(255,255,255,0.3)',
              },
              '100%': {
                opacity: 1,
                transform: 'scale(1.3)',
                boxShadow: '0 0 20px rgba(255,255,255,0.8)',
              },
            },
          }}
        >
          {/* Animated rotating star on top */}
          <Box
            sx={{
              top: '5%',
              left: '45%',
              width: '10%',
              height: '10%',
              backgroundColor: '#F59E0B',
              borderRadius: 0,
              clipPath:
                'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
              animation: 'starTwinkle 0.8s ease-in-out infinite, starRotate 4s linear infinite',
              '@keyframes starTwinkle': {
                '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.2, transform: 'scale(1.4)' },
              },
              '@keyframes starRotate': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />

          {/* Tree layers with staggered animations */}
          <Box
            sx={{
              top: '20%',
              left: '25%',
              width: '15%',
              height: '15%',
              backgroundColor: '#DC2626',
              animationDelay: '0s',
            }}
          />
          <Box
            sx={{
              top: '20%',
              left: '60%',
              width: '15%',
              height: '15%',
              backgroundColor: '#16A34A',
              animationDelay: '0.3s',
            }}
          />
          <Box
            sx={{
              top: '40%',
              left: '15%',
              width: '15%',
              height: '15%',
              backgroundColor: '#16A34A',
              animationDelay: '0.6s',
            }}
          />
          <Box
            sx={{
              top: '40%',
              left: '45%',
              width: '15%',
              height: '15%',
              backgroundColor: '#F59E0B',
              animationDelay: '0.9s',
            }}
          />
          <Box
            sx={{
              top: '40%',
              left: '70%',
              width: '15%',
              height: '15%',
              backgroundColor: '#DC2626',
              animationDelay: '1.2s',
            }}
          />
          <Box
            sx={{
              top: '60%',
              left: '35%',
              width: '15%',
              height: '15%',
              backgroundColor: '#DC2626',
              animationDelay: '1.5s',
            }}
          />
          <Box
            sx={{
              top: '60%',
              left: '50%',
              width: '15%',
              height: '15%',
              backgroundColor: '#16A34A',
              animationDelay: '1.8s',
            }}
          />

          {/* Tree trunk with subtle sway */}
          <Box
            sx={{
              bottom: '10%',
              left: '42%',
              width: '16%',
              height: '25%',
              backgroundColor: '#92400E',
              borderRadius: '2px',
              animation: 'treeSway 3s ease-in-out infinite',
              '@keyframes treeSway': {
                '0%, 100%': { transform: 'rotate(0deg)' },
                '25%': { transform: 'rotate(1deg)' },
                '75%': { transform: 'rotate(-1deg)' },
              },
            }}
          />
        </Box>
      </Box>
      {label && (
        <Typography variant="body2" sx={{ color: '#16A34A', fontWeight: 'bold' }}>
          {label}
        </Typography>
      )}
    </Box>
  )
}

// Snowflake Loader Component - Enhanced detailed crystalline structure
const SnowflakeLoader = ({ size = 40, label }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: size * 2,
          height: size * 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Mystical ice aura */}
        <Box
          sx={{
            position: 'absolute',
            width: '130%',
            height: '130%',
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(14, 165, 233, 0.1) 0%, rgba(125, 211, 252, 0.05) 50%, transparent 70%)',
            animation: 'iceAura 4s ease-in-out infinite',
            '@keyframes iceAura': {
              '0%, 100%': { opacity: 0.3, transform: 'scale(1) rotate(0deg)' },
              '50%': { opacity: 0.8, transform: 'scale(1.3) rotate(180deg)' },
            },
          }}
        />

        {/* Floating ice crystals around main snowflake */}
        {[...Array(8)].map((_, index) => (
          <Box
            key={`crystal-${index}`}
            sx={{
              position: 'absolute',
              width: '6px',
              height: '6px',
              backgroundColor: '#E0F2FE',
              borderRadius: '50%',
              border: '1px solid #0EA5E9',
              top: '50%',
              left: '50%',
              transformOrigin: '0 0',
              animation: `orbitCrystal${index} ${4 + index * 0.5}s linear infinite`,
              [`@keyframes orbitCrystal${index}`]: {
                '0%': {
                  transform: `translate(-50%, -50%) rotate(${index * 45}deg) translateX(${size + 20}px) scale(0.5)`,
                  opacity: 0.4,
                },
                '50%': {
                  opacity: 1,
                  transform: `translate(-50%, -50%) rotate(${index * 45 + 180}deg) translateX(${size + 20}px) scale(1)`,
                },
                '100%': {
                  transform: `translate(-50%, -50%) rotate(${index * 45 + 360}deg) translateX(${size + 20}px) scale(0.5)`,
                  opacity: 0.4,
                },
              },
            }}
          />
        ))}

        {/* Detailed snowflake crystal with 6-fold symmetry */}
        <Box
          sx={{
            position: 'relative',
            width: '90%',
            height: '90%',
            animation: 'snowflakeRotate 6s linear infinite',
            '@keyframes snowflakeRotate': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        >
          {/* 6 main arms of the snowflake */}
          {[...Array(6)].map((_, armIndex) => (
            <Box
              key={`arm-${armIndex}`}
              sx={{
                position: 'absolute',
                top: '48%',
                left: '50%',
                width: '50%',
                height: '4%',
                backgroundColor: '#0EA5E9',
                borderRadius: '2px',
                transformOrigin: '0 50%',
                transform: `rotate(${armIndex * 60}deg)`,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  right: '20%',
                  top: '-100%',
                  width: '30%',
                  height: '300%',
                  backgroundColor: '#7DD3FC',
                  borderRadius: '1px',
                  transform: 'rotate(30deg)',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: '20%',
                  top: '-100%',
                  width: '30%',
                  height: '300%',
                  backgroundColor: '#7DD3FC',
                  borderRadius: '1px',
                  transform: 'rotate(-30deg)',
                },
              }}
            />
          ))}

          {/* Secondary crystalline branches */}
          {[...Array(6)].map((_, branchIndex) => (
            <Box
              key={`branch-${branchIndex}`}
              sx={{
                position: 'absolute',
                top: '47%',
                left: '50%',
                width: '35%',
                height: '6%',
                background: 'linear-gradient(90deg, #0EA5E9 0%, #7DD3FC 70%, transparent 100%)',
                borderRadius: '3px',
                transformOrigin: '0 50%',
                transform: `rotate(${branchIndex * 60 + 30}deg)`,
                animation: `branchPulse${branchIndex} 2s ease-in-out infinite`,
                [`@keyframes branchPulse${branchIndex}`]: {
                  '0%, 100%': {
                    opacity: 0.6,
                    transform: `rotate(${branchIndex * 60 + 30}deg) scale(1)`,
                  },
                  '50%': {
                    opacity: 1,
                    transform: `rotate(${branchIndex * 60 + 30}deg) scale(1.15)`,
                  },
                },
                animationDelay: `${branchIndex * 0.3}s`,
              }}
            />
          ))}

          {/* Intricate center crystal with layered hexagon */}
          <Box
            sx={{
              position: 'absolute',
              top: '35%',
              left: '35%',
              width: '30%',
              height: '30%',
              backgroundColor: '#F0F9FF',
              border: '3px solid #0EA5E9',
              borderRadius: '10%',
              clipPath: 'polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)',
              animation: 'hexagonalPulse 2.5s ease-in-out infinite',
              '@keyframes hexagonalPulse': {
                '0%, 100%': {
                  opacity: 0.7,
                  transform: 'scale(1) rotate(0deg)',
                  boxShadow: '0 0 10px rgba(14, 165, 233, 0.5)',
                },
                '33%': {
                  opacity: 1,
                  transform: 'scale(1.2) rotate(120deg)',
                  boxShadow: '0 0 25px rgba(14, 165, 233, 0.9)',
                },
                '66%': {
                  opacity: 0.8,
                  transform: 'scale(0.9) rotate(240deg)',
                  boxShadow: '0 0 15px rgba(125, 211, 252, 0.7)',
                },
              },
            }}
          />

          {/* Inner crystalline star */}
          <Box
            sx={{
              position: 'absolute',
              top: '42%',
              left: '42%',
              width: '16%',
              height: '16%',
              backgroundColor: '#DBEAFE',
              border: '2px solid #38BDF8',
              borderRadius: '50%',
              animation: 'innerCrystalSpin 3s linear infinite reverse',
              '@keyframes innerCrystalSpin': {
                '0%': { transform: 'rotate(0deg) scale(1)' },
                '50%': { transform: 'rotate(180deg) scale(1.3)' },
                '100%': { transform: 'rotate(360deg) scale(1)' },
              },
            }}
          />
        </Box>
      </Box>
      {label && (
        <Typography variant="body2" sx={{ color: '#0EA5E9', fontWeight: 'bold' }}>
          {label}
        </Typography>
      )}
    </Box>
  )
}

// Summer Loader Component
const SummerLoader = ({ size = 40, label }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: size * 2,
          height: size * 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Sun rays */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            animation: 'sunRaysRotate 4s linear infinite',
            '@keyframes sunRaysRotate': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        >
          {/* Long rays */}
          {[...Array(8)].map((_, index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                top: '47%',
                left: '10%',
                width: '30%',
                height: '6%',
                backgroundColor: '#FDE047',
                borderRadius: '3px',
                transformOrigin: '100% 50%',
                transform: `rotate(${index * 45}deg)`,
              }}
            />
          ))}
        </Box>

        {/* Sun center */}
        <Box
          sx={{
            width: '50%',
            height: '50%',
            backgroundColor: '#FB923C',
            borderRadius: '50%',
            position: 'relative',
            animation: 'sunPulse 2s ease-in-out infinite',
            '@keyframes sunPulse': {
              '0%, 100%': {
                backgroundColor: '#FB923C',
                boxShadow: '0 0 20px rgba(251, 146, 60, 0.6)',
              },
              '50%': {
                backgroundColor: '#FDE047',
                boxShadow: '0 0 30px rgba(253, 224, 71, 0.8)',
              },
            },
          }}
        >
          {/* Sun face */}
          <Box
            sx={{
              position: 'absolute',
              top: '25%',
              left: '25%',
              width: '15%',
              height: '15%',
              backgroundColor: '#EA580C',
              borderRadius: '50%',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '25%',
              right: '25%',
              width: '15%',
              height: '15%',
              backgroundColor: '#EA580C',
              borderRadius: '50%',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '25%',
              left: '30%',
              right: '30%',
              height: '8%',
              backgroundColor: '#EA580C',
              borderRadius: '0 0 20px 20px',
            }}
          />
        </Box>
      </Box>
      {label && (
        <Typography variant="body2" sx={{ color: '#FB923C', fontWeight: 'bold' }}>
          {label}
        </Typography>
      )}
    </Box>
  )
}

// Valentine's Day Loader Component - Floating Love Hearts
const ValentinesLoader = ({ size = 40, label }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: size * 2,
          height: size * 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Central pulsing text */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 3,
            animation: 'lovePulse 2s ease-in-out infinite',
            '@keyframes lovePulse': {
              '0%, 100%': {
                transform: 'translate(-50%, -50%) scale(1)',
                opacity: 1,
              },
              '50%': {
                transform: 'translate(-50%, -50%) scale(1.1)',
                opacity: 0.8,
              },
            },
          }}
        >
          <Typography
            sx={{
              fontSize: `${size * 0.4}px`,
              fontWeight: 'bold',
              color: '#E91E63',
              textShadow: '0 2px 4px rgba(233, 30, 99, 0.3)',
              letterSpacing: '1px',
            }}
          >
            LOVE
          </Typography>
        </Box>

        {/* Floating hearts - different sizes and timing */}
        {[...Array(8)].map((_, index) => {
          const heartSize = 8 + (index % 3) * 4 // Varying sizes: 8px, 12px, 16px
          const startAngle = (index * 45) % 360 // Distribute around circle
          const distance = size * 0.6 + (index % 2) * 10 // Varying distances

          return (
            <Box
              key={`love-heart-${index}`}
              sx={{
                position: 'absolute',
                width: `${heartSize}px`,
                height: `${heartSize * 0.9}px`,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1,
                animation: `floatingLove${index} ${2.5 + (index % 3) * 0.5}s ease-in-out infinite`,
                [`@keyframes floatingLove${index}`]: {
                  '0%': {
                    transform: `translate(-50%, -50%) rotate(${startAngle}deg) translateY(0px) scale(0)`,
                    opacity: 0,
                  },
                  '15%': {
                    opacity: 1,
                    transform: `translate(-50%, -50%) rotate(${startAngle}deg) translateY(0px) scale(1)`,
                  },
                  '85%': {
                    opacity: 1,
                    transform: `translate(-50%, -50%) rotate(${startAngle}deg) translateY(-${distance}px) scale(0.8)`,
                  },
                  '100%': {
                    opacity: 0,
                    transform: `translate(-50%, -50%) rotate(${startAngle}deg) translateY(-${distance + 20}px) scale(0)`,
                  },
                },
                animationDelay: `${index * 0.3}s`,

                // Heart shape using pseudo-elements
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  width: '50%',
                  height: '80%',
                  background: index % 2 === 0 ? '#FF69B4' : '#FF1493', // Alternating pink shades
                  borderRadius: '50% 50% 0 0',
                  transform: 'translate(-50%, 0) rotate(-45deg)',
                  transformOrigin: '0 100%',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  width: '50%',
                  height: '80%',
                  background: index % 2 === 0 ? '#FF69B4' : '#FF1493', // Alternating pink shades
                  borderRadius: '50% 50% 0 0',
                  transform: 'translate(-50%, 0) rotate(45deg)',
                  transformOrigin: '100% 100%',
                },
              }}
            />
          )
        })}

        {/* Subtle sparkles for extra magic */}
        {[...Array(6)].map((_, index) => (
          <Box
            key={`sparkle-${index}`}
            sx={{
              position: 'absolute',
              width: '3px',
              height: '3px',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: '#FFD700',
              borderRadius: '50%',
              animation: `sparkle${index} ${1.8 + index * 0.2}s ease-in-out infinite`,
              [`@keyframes sparkle${index}`]: {
                '0%': {
                  transform: `translate(-50%, -50%) rotate(${index * 60}deg) translateX(${size * 0.8}px) scale(0)`,
                  opacity: 0,
                },
                '50%': {
                  opacity: 1,
                  transform: `translate(-50%, -50%) rotate(${index * 60 + 180}deg) translateX(${size * 0.8}px) scale(1)`,
                },
                '100%': {
                  opacity: 0,
                  transform: `translate(-50%, -50%) rotate(${index * 60 + 360}deg) translateX(${size * 0.8}px) scale(0)`,
                },
              },
              animationDelay: `${index * 0.5}s`,
            }}
          />
        ))}

        {/* Soft circular glow behind everything */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: size * 1.5,
            height: size * 1.5,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 105, 180, 0.1) 0%, transparent 60%)',
            animation: 'gentleGlow 3s ease-in-out infinite',
            zIndex: 0,
            '@keyframes gentleGlow': {
              '0%, 100%': {
                opacity: 0.3,
                transform: 'translate(-50%, -50%) scale(1)',
              },
              '50%': {
                opacity: 0.6,
                transform: 'translate(-50%, -50%) scale(1.2)',
              },
            },
          }}
        />
      </Box>

      {label && (
        <Typography
          variant="body2"
          sx={{
            color: '#E91E63',
            textAlign: 'center',
            fontWeight: '600',
            textShadow: '0 1px 3px rgba(233, 30, 99, 0.3)',
            fontSize: '0.875rem',
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  )
}

// New Year Loader Component - "HNY" Fireworks
const NewYearLoader = ({ size = 40, label }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: size * 2.5,
          height: size * 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Central "HNY" text with sparkle effect */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 3,
            animation: 'hnySparkle 3s ease-in-out infinite',
            '@keyframes hnySparkle': {
              '0%, 100%': {
                transform: 'translate(-50%, -50%) scale(1)',
                textShadow: '0 0 20px #FFD700, 0 0 30px #FFA500, 0 0 40px #FF6347',
              },
              '50%': {
                transform: 'translate(-50%, -50%) scale(1.15)',
                textShadow: '0 0 30px #FFD700, 0 0 50px #FFA500, 0 0 60px #FF6347',
              },
            },
          }}
        >
          <Typography
            sx={{
              fontSize: `${size * 0.35}px`,
              fontWeight: 'bold',
              color: '#FFD700',
              fontFamily: 'serif',
              letterSpacing: '2px',
            }}
          >
            HNY
          </Typography>
        </Box>

        {/* Fireworks explosions - multiple bursts */}
        {[...Array(12)].map((_, index) => (
          <Box
            key={`firework-${index}`}
            sx={{
              position: 'absolute',
              width: '4px',
              height: '4px',
              backgroundColor:
                index % 3 === 0 ? '#FFD700' : index % 3 === 1 ? '#FF6347' : '#00BFFF',
              borderRadius: '50%',
              top: '50%',
              left: '50%',
              transformOrigin: '50% 50%',
              animation: `fireworkBurst${index} 2s ease-out infinite`,
              animationDelay: `${index * 0.1 + (index % 4) * 0.5}s`,
              [`@keyframes fireworkBurst${index}`]: {
                '0%': {
                  transform: 'translate(-50%, -50%) scale(0)',
                  opacity: 0,
                },
                '10%': {
                  transform: `translate(-50%, -50%) translate(${Math.cos((index * 30 * Math.PI) / 180) * 20}px, ${Math.sin((index * 30 * Math.PI) / 180) * 20}px) scale(1)`,
                  opacity: 1,
                },
                '50%': {
                  transform: `translate(-50%, -50%) translate(${Math.cos((index * 30 * Math.PI) / 180) * 60}px, ${Math.sin((index * 30 * Math.PI) / 180) * 60}px) scale(1.2)`,
                  opacity: 0.8,
                },
                '100%': {
                  transform: `translate(-50%, -50%) translate(${Math.cos((index * 30 * Math.PI) / 180) * 100}px, ${Math.sin((index * 30 * Math.PI) / 180) * 100}px) scale(0)`,
                  opacity: 0,
                },
              },
            }}
          />
        ))}

        {/* Trailing sparks */}
        {[...Array(20)].map((_, index) => (
          <Box
            key={`spark-${index}`}
            sx={{
              position: 'absolute',
              width: '2px',
              height: '8px',
              backgroundColor: '#FFD700',
              borderRadius: '1px',
              top: '50%',
              left: '50%',
              transformOrigin: '50% 100%',
              animation: `sparkTrail${index} 1.5s ease-out infinite`,
              animationDelay: `${index * 0.05}s`,
              [`@keyframes sparkTrail${index}`]: {
                '0%': {
                  transform: `translate(-50%, -50%) rotate(${index * 18}deg) translateY(0px) scale(0)`,
                  opacity: 0,
                },
                '20%': {
                  transform: `translate(-50%, -50%) rotate(${index * 18}deg) translateY(-20px) scale(1)`,
                  opacity: 1,
                },
                '80%': {
                  transform: `translate(-50%, -50%) rotate(${index * 18}deg) translateY(-60px) scale(0.5)`,
                  opacity: 0.6,
                },
                '100%': {
                  transform: `translate(-50%, -50%) rotate(${index * 18}deg) translateY(-100px) scale(0)`,
                  opacity: 0,
                },
              },
            }}
          />
        ))}
      </Box>

      {/* Optional label */}
      {label && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            fontSize: '0.875rem',
            marginTop: 1,
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  )
}

// Fourth of July Loader Component - Red & Blue Fireworks
const FourthOfJulyLoader = ({ size = 40, label }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: size * 2.5,
          height: size * 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Central star with patriotic colors */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 3,
            animation: 'patrioticPulse 2s ease-in-out infinite',
            '@keyframes patrioticPulse': {
              '0%, 100%': {
                transform: 'translate(-50%, -50%) scale(1) rotate(0deg)',
                filter: 'drop-shadow(0 0 10px #FF0000) drop-shadow(0 0 20px #0000FF)',
              },
              '50%': {
                transform: 'translate(-50%, -50%) scale(1.2) rotate(180deg)',
                filter: 'drop-shadow(0 0 20px #FF0000) drop-shadow(0 0 30px #0000FF)',
              },
            },
          }}
        >
          <Box
            sx={{
              width: `${size * 0.8}px`,
              height: `${size * 0.8}px`,
              background:
                'linear-gradient(45deg, #FF0000 25%, #FFFFFF 25%, #FFFFFF 50%, #0000FF 50%, #0000FF 75%, #FF0000 75%)',
              clipPath:
                'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
            }}
          />
        </Box>

        {/* Red fireworks bursts */}
        {[...Array(8)].map((_, index) => (
          <Box
            key={`red-firework-${index}`}
            sx={{
              position: 'absolute',
              width: '6px',
              height: '6px',
              backgroundColor: '#FF0000',
              borderRadius: '50%',
              top: '50%',
              left: '50%',
              transformOrigin: '50% 50%',
              animation: `redBurst${index} 2.5s ease-out infinite`,
              animationDelay: `${index * 0.1}s`,
              [`@keyframes redBurst${index}`]: {
                '0%': {
                  transform: 'translate(-50%, -50%) scale(0)',
                  opacity: 0,
                },
                '15%': {
                  transform: `translate(-50%, -50%) translate(${Math.cos((index * 45 * Math.PI) / 180) * 30}px, ${Math.sin((index * 45 * Math.PI) / 180) * 30}px) scale(1)`,
                  opacity: 1,
                },
                '60%': {
                  transform: `translate(-50%, -50%) translate(${Math.cos((index * 45 * Math.PI) / 180) * 80}px, ${Math.sin((index * 45 * Math.PI) / 180) * 80}px) scale(1.5)`,
                  opacity: 0.7,
                },
                '100%': {
                  transform: `translate(-50%, -50%) translate(${Math.cos((index * 45 * Math.PI) / 180) * 120}px, ${Math.sin((index * 45 * Math.PI) / 180) * 120}px) scale(0)`,
                  opacity: 0,
                },
              },
            }}
          />
        ))}

        {/* Blue fireworks bursts */}
        {[...Array(8)].map((_, index) => (
          <Box
            key={`blue-firework-${index}`}
            sx={{
              position: 'absolute',
              width: '6px',
              height: '6px',
              backgroundColor: '#0000FF',
              borderRadius: '50%',
              top: '50%',
              left: '50%',
              transformOrigin: '50% 50%',
              animation: `blueBurst${index} 2.5s ease-out infinite`,
              animationDelay: `${index * 0.1 + 1.25}s`,
              [`@keyframes blueBurst${index}`]: {
                '0%': {
                  transform: 'translate(-50%, -50%) scale(0)',
                  opacity: 0,
                },
                '15%': {
                  transform: `translate(-50%, -50%) translate(${Math.cos(((index + 22.5) * 45 * Math.PI) / 180) * 30}px, ${Math.sin(((index + 22.5) * 45 * Math.PI) / 180) * 30}px) scale(1)`,
                  opacity: 1,
                },
                '60%': {
                  transform: `translate(-50%, -50%) translate(${Math.cos(((index + 22.5) * 45 * Math.PI) / 180) * 80}px, ${Math.sin(((index + 22.5) * 45 * Math.PI) / 180) * 80}px) scale(1.5)`,
                  opacity: 0.7,
                },
                '100%': {
                  transform: `translate(-50%, -50%) translate(${Math.cos(((index + 22.5) * 45 * Math.PI) / 180) * 120}px, ${Math.sin(((index + 22.5) * 45 * Math.PI) / 180) * 120}px) scale(0)`,
                  opacity: 0,
                },
              },
            }}
          />
        ))}

        {/* White sparkling effects */}
        {[...Array(15)].map((_, index) => (
          <Box
            key={`white-spark-${index}`}
            sx={{
              position: 'absolute',
              width: '3px',
              height: '3px',
              backgroundColor: '#FFFFFF',
              borderRadius: '50%',
              top: '50%',
              left: '50%',
              animation: `whiteSparkle${index} 1s ease-in-out infinite`,
              animationDelay: `${index * 0.067}s`,
              [`@keyframes whiteSparkle${index}`]: {
                '0%, 100%': {
                  transform: `translate(-50%, -50%) translate(${((index % 5) - 2) * 15}px, ${Math.floor(index / 5 - 1) * 15}px) scale(0)`,
                  opacity: 0,
                },
                '50%': {
                  transform: `translate(-50%, -50%) translate(${((index % 5) - 2) * 15}px, ${Math.floor(index / 5 - 1) * 15}px) scale(1)`,
                  opacity: 1,
                },
              },
            }}
          />
        ))}
      </Box>

      {/* Optional label */}
      {label && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            fontSize: '0.875rem',
            marginTop: 1,
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  )
}

// Chinese New Year Loader Component - Fire Horse 2026
const ChineseNewYearLoader = ({ size = 40, label }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: size * 2.5,
          height: size * 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Central horse silhouette with fire effect */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 3,
            animation: 'fireHorseGallop 3s ease-in-out infinite',
            '@keyframes fireHorseGallop': {
              '0%, 100%': {
                transform: 'translate(-50%, -50%) scale(1)',
                filter: 'drop-shadow(0 0 15px #FF4500) drop-shadow(0 0 25px #FFD700)',
              },
              '50%': {
                transform: 'translate(-50%, -50%) scale(1.1)',
                filter: 'drop-shadow(0 0 25px #FF4500) drop-shadow(0 0 35px #FFD700)',
              },
            },
          }}
        >
          {/* Horse SVG-like shape using CSS */}
          <Box
            sx={{
              width: `${size * 1.2}px`,
              height: `${size * 1}px`,
              background: 'linear-gradient(45deg, #FF4500, #FFD700, #FF6347)',
              clipPath:
                'polygon(20% 80%, 25% 50%, 30% 30%, 40% 20%, 50% 15%, 60% 20%, 70% 30%, 75% 50%, 80% 80%, 70% 85%, 60% 90%, 50% 95%, 40% 90%, 30% 85%)',
              position: 'relative',
            }}
          >
            {/* Horse mane effect */}
            <Box
              sx={{
                position: 'absolute',
                top: '-10px',
                left: '40%',
                width: '30px',
                height: '20px',
                background: 'linear-gradient(90deg, #FF4500, #FFD700)',
                clipPath: 'polygon(0% 100%, 20% 0%, 40% 50%, 60% 0%, 80% 70%, 100% 20%, 80% 100%)',
                animation: 'maneFlicker 1.5s ease-in-out infinite',
                '@keyframes maneFlicker': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.7 },
                },
              }}
            />
          </Box>
        </Box>

        {/* Fire particles around horse */}
        {[...Array(16)].map((_, index) => (
          <Box
            key={`fire-particle-${index}`}
            sx={{
              position: 'absolute',
              width: `${3 + (index % 3)}px`,
              height: `${6 + (index % 4)}px`,
              backgroundColor:
                index % 3 === 0 ? '#FF4500' : index % 3 === 1 ? '#FFD700' : '#FF6347',
              borderRadius: '50% 50% 50% 0',
              top: '50%',
              left: '50%',
              transformOrigin: '50% 100%',
              animation: `fireParticle${index} 2s ease-out infinite`,
              animationDelay: `${index * 0.125}s`,
              [`@keyframes fireParticle${index}`]: {
                '0%': {
                  transform: `translate(-50%, -50%) translate(${((index % 8) - 4) * 8}px, ${Math.floor(index / 8) * 8}px) scale(0) rotate(0deg)`,
                  opacity: 0,
                },
                '20%': {
                  transform: `translate(-50%, -50%) translate(${((index % 8) - 4) * 15}px, ${Math.floor(index / 8) * 15}px) scale(1) rotate(45deg)`,
                  opacity: 1,
                },
                '80%': {
                  transform: `translate(-50%, -50%) translate(${((index % 8) - 4) * 40}px, ${Math.floor(index / 8) * 40 - 30}px) scale(0.5) rotate(90deg)`,
                  opacity: 0.6,
                },
                '100%': {
                  transform: `translate(-50%, -50%) translate(${((index % 8) - 4) * 60}px, ${Math.floor(index / 8) * 60 - 50}px) scale(0) rotate(135deg)`,
                  opacity: 0,
                },
              },
            }}
          />
        ))}

        {/* Golden Chinese characters or symbols */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#FFD700',
            fontSize: `${size * 0.3}px`,
            fontWeight: 'bold',
            animation: 'chineseGlow 2s ease-in-out infinite',
            '@keyframes chineseGlow': {
              '0%, 100%': {
                textShadow: '0 0 10px #FFD700',
                opacity: 1,
              },
              '50%': {
                textShadow: '0 0 20px #FFD700, 0 0 30px #FF4500',
                opacity: 0.8,
              },
            },
          }}
        >
          馬
        </Box>
      </Box>

      {/* Optional label */}
      {label && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            fontSize: '0.875rem',
            marginTop: 1,
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  )
}

// Diwali Loader Component - Flickering Diyas
const DiwaliLoader = ({ size = 40, label }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: size * 2.5,
          height: size * 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Central arrangement of diyas (oil lamps) */}
        {[...Array(5)].map((_, index) => (
          <Box
            key={`diya-${index}`}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: `translate(-50%, -50%) translate(${Math.cos((index * 72 * Math.PI) / 180) * 30}px, ${Math.sin((index * 72 * Math.PI) / 180) * 30}px)`,
              zIndex: 2,
            }}
          >
            {/* Diya base */}
            <Box
              sx={{
                width: `${size * 0.4}px`,
                height: `${size * 0.2}px`,
                backgroundColor: '#8B4513',
                borderRadius: '50% 50% 20% 20%',
                position: 'relative',
                animation: `diyaGlow${index} 2s ease-in-out infinite`,
                animationDelay: `${index * 0.2}s`,
                [`@keyframes diyaGlow${index}`]: {
                  '0%, 100%': {
                    filter: 'brightness(1)',
                    transform: 'scale(1)',
                  },
                  '50%': {
                    filter: 'brightness(1.2)',
                    transform: 'scale(1.05)',
                  },
                },
              }}
            >
              {/* Flame */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '8px',
                  height: '16px',
                  background: 'linear-gradient(to top, #FFD700 0%, #FF6347 50%, #FF4500 100%)',
                  borderRadius: '50% 50% 50% 0',
                  animation: `flameFlicker${index} 1s ease-in-out infinite`,
                  animationDelay: `${index * 0.1}s`,
                  [`@keyframes flameFlicker${index}`]: {
                    '0%, 100%': {
                      transform: 'translateX(-50%) scale(1) rotate(-2deg)',
                      opacity: 1,
                    },
                    '25%': {
                      transform: 'translateX(-50%) scale(1.1) rotate(2deg)',
                      opacity: 0.9,
                    },
                    '50%': {
                      transform: 'translateX(-50%) scale(0.9) rotate(-1deg)',
                      opacity: 0.8,
                    },
                    '75%': {
                      transform: 'translateX(-50%) scale(1.05) rotate(1deg)',
                      opacity: 0.95,
                    },
                  },
                }}
              />

              {/* Light glow effect */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '-20px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '30px',
                  height: '30px',
                  background:
                    'radial-gradient(circle, rgba(255, 215, 0, 0.3) 0%, rgba(255, 99, 71, 0.2) 50%, transparent 70%)',
                  borderRadius: '50%',
                  animation: `lightGlow${index} 2s ease-in-out infinite`,
                  animationDelay: `${index * 0.15}s`,
                  [`@keyframes lightGlow${index}`]: {
                    '0%, 100%': {
                      transform: 'translateX(-50%) scale(1)',
                      opacity: 0.6,
                    },
                    '50%': {
                      transform: 'translateX(-50%) scale(1.3)',
                      opacity: 0.9,
                    },
                  },
                }}
              />
            </Box>
          </Box>
        ))}

        {/* Floating light particles */}
        {[...Array(20)].map((_, index) => (
          <Box
            key={`light-particle-${index}`}
            sx={{
              position: 'absolute',
              width: '3px',
              height: '3px',
              backgroundColor:
                index % 4 === 0
                  ? '#FFD700'
                  : index % 4 === 1
                    ? '#FF6347'
                    : index % 4 === 2
                      ? '#FFA500'
                      : '#FFFF00',
              borderRadius: '50%',
              top: '50%',
              left: '50%',
              animation: `lightParticle${index} 3s ease-out infinite`,
              animationDelay: `${index * 0.15}s`,
              [`@keyframes lightParticle${index}`]: {
                '0%': {
                  transform: `translate(-50%, -50%) translate(0px, 0px) scale(0)`,
                  opacity: 0,
                },
                '20%': {
                  transform: `translate(-50%, -50%) translate(${((index % 10) - 5) * 10}px, ${Math.floor(index / 10) * 10}px) scale(1)`,
                  opacity: 1,
                },
                '80%': {
                  transform: `translate(-50%, -50%) translate(${((index % 10) - 5) * 40}px, ${Math.floor(index / 10) * 40 - 20}px) scale(0.5)`,
                  opacity: 0.7,
                },
                '100%': {
                  transform: `translate(-50%, -50%) translate(${((index % 10) - 5) * 60}px, ${Math.floor(index / 10) * 60 - 40}px) scale(0)`,
                  opacity: 0,
                },
              },
            }}
          />
        ))}

        {/* Central decorative pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#FFD700',
            fontSize: `${size * 0.25}px`,
            fontWeight: 'bold',
            animation: 'diwaliGlow 2.5s ease-in-out infinite',
            '@keyframes diwaliGlow': {
              '0%, 100%': {
                textShadow: '0 0 10px #FFD700, 0 0 20px #FF6347',
                transform: 'translate(-50%, -50%) rotate(0deg)',
              },
              '50%': {
                textShadow: '0 0 20px #FFD700, 0 0 30px #FF6347, 0 0 40px #FFA500',
                transform: 'translate(-50%, -50%) rotate(180deg)',
              },
            },
          }}
        >
          ✦
        </Box>
      </Box>

      {/* Optional label */}
      {label && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            fontSize: '0.875rem',
            marginTop: 1,
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  )
}

// Default Hapana Loader Component
const DefaultLoader = ({ size = 40, label }) => {
  // Using CSS fallback since lottie path has issues
  const Player = null
  const hapanaLogoAnimation = null

  // Skip Lottie for now and use the CSS-based loader
  if (false && Player && hapanaLogoAnimation) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Player
          autoplay
          loop
          src={hapanaLogoAnimation}
          style={{
            height: size * 2,
            width: size * 2,
          }}
          speed={1.2}
        />
        {label && (
          <Typography variant="body2" sx={{ color: '#4A90E2', fontWeight: 'bold' }}>
            {label}
          </Typography>
        )}
      </Box>
    )
  }

  // Fallback: Custom Hapana-themed loader using CSS
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: size * 2,
          height: size * 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Hapana "H" logo made with geometric shapes */}
        <Box
          sx={{
            position: 'relative',
            width: '80%',
            height: '80%',
            animation: 'hapanaRotate 2s linear infinite',
            '@keyframes hapanaRotate': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        >
          {/* Left vertical bar of "H" */}
          <Box
            sx={{
              position: 'absolute',
              left: '15%',
              top: '10%',
              bottom: '10%',
              width: '8%',
              backgroundColor: '#4A90E2',
              borderRadius: '4px',
            }}
          />

          {/* Right vertical bar of "H" */}
          <Box
            sx={{
              position: 'absolute',
              right: '15%',
              top: '10%',
              bottom: '10%',
              width: '8%',
              backgroundColor: '#4A90E2',
              borderRadius: '4px',
            }}
          />

          {/* Horizontal bar of "H" */}
          <Box
            sx={{
              position: 'absolute',
              left: '15%',
              right: '15%',
              top: '46%',
              height: '8%',
              backgroundColor: '#4A90E2',
              borderRadius: '4px',
            }}
          />

          {/* Center pulse effect */}
          <Box
            sx={{
              position: 'absolute',
              top: '40%',
              left: '40%',
              width: '20%',
              height: '20%',
              backgroundColor: '#4A90E2',
              borderRadius: '50%',
              animation: 'hapanapulse 1.5s ease-in-out infinite',
              '@keyframes hapanapulse': {
                '0%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                '50%': { opacity: 0.8, transform: 'scale(1.2)' },
              },
            }}
          />
        </Box>
      </Box>
      {label && (
        <Typography variant="body2" sx={{ color: '#4A90E2', fontWeight: 'bold' }}>
          {label}
        </Typography>
      )}
    </Box>
  )
}

/**
 * Contextual loading messages for different operations
 */
const CONTEXTUAL_MESSAGES = {
  corporates: {
    fetch: (count) => `Loading corporate data${count ? ` (${count} items)` : ''}...`,
    create: () => 'Creating corporate profile...',
    update: () => 'Updating corporate settings...',
    delete: () => 'Removing corporate data...',
    process: () => 'Processing corporate configurations...',
  },
  users: {
    fetch: (count) => `Loading users${count ? ` (${count} items)` : ''}...`,
    create: () => 'Creating user account...',
    update: () => 'Updating user profile...',
    delete: () => 'Removing user account...',
    authenticate: () => 'Authenticating user...',
    provision: () => 'Provisioning user access...',
  },
  sites: {
    fetch: (count) => `Loading gym locations${count ? ` (${count} items)` : ''}...`,
    create: () => 'Setting up new gym location...',
    update: () => 'Updating gym settings...',
    delete: () => 'Removing gym location...',
    configure: () => 'Applying gym configuration...',
  },
  packages: {
    fetch: (count) => `Loading membership packages${count ? ` (${count} items)` : ''}...`,
    create: () => 'Creating membership package...',
    update: () => 'Updating package details...',
    delete: () => 'Removing membership package...',
    calculate: () => 'Calculating package pricing...',
  },
  sessions: {
    fetch: (count) => `Loading class sessions${count ? ` (${count} items)` : ''}...`,
    create: () => 'Scheduling new class session...',
    update: () => 'Updating session details...',
    delete: () => 'Cancelling class session...',
    book: () => 'Processing class booking...',
  },
  reports: {
    fetch: () => 'Generating report data...',
    export: () => 'Preparing data export...',
    calculate: () => 'Computing analytics...',
    aggregate: () => 'Aggregating metrics...',
  },
  default: {
    fetch: (count) => `Loading data${count ? ` (${count} items)` : ''}...`,
    create: () => 'Creating new item...',
    update: () => 'Saving changes...',
    delete: () => 'Removing item...',
    process: () => 'Processing request...',
  },
}

/**
 * Generate contextual loading message
 */
const generateContextualMessage = (context, operation, itemCount) => {
  const contextMessages = CONTEXTUAL_MESSAGES[context] || CONTEXTUAL_MESSAGES.default
  const messageGenerator = contextMessages[operation] || contextMessages.process
  return typeof messageGenerator === 'function' ? messageGenerator(itemCount) : messageGenerator
}

/**
 * Hapana Loading Component with Dynamic Themed Loaders
 *
 * Displays contextual loading states with seasonal theme variations.
 * Automatically selects appropriate loader based on current date and manual overrides.
 */
const Loading = ({
  label,
  buttonLabel = false,
  forceFallback = false,
  context,
  countryCode,
  brandId,
  region,
  loaderOverride,
  disableDynamicLoaders = false,
  operation,
  itemCount,
  size,
}) => {
  /**
   * Generate contextual loading props
   */
  const baseProps = useMemo(() => {
    // Generate contextual message if not provided
    const contextualLabel =
      label ||
      (context && operation ? generateContextualMessage(context, operation, itemCount) : null)

    return {
      size: size || 24,
      label: contextualLabel,
      context: context || 'default',
      countryCode,
      brandId,
      region,
    }
  }, [label, context, operation, itemCount, countryCode, brandId, region, size])

  /**
   * Get dynamic loader component
   */
  const { LoaderComponent, loaderProps } = useMemo(() => {
    return {
      LoaderComponent: HapanaLoader,
      loaderProps: baseProps,
    }
  }, [baseProps])

  if (LoaderComponent) {
    return (
      <Box
        className={`hapana-loading dynamic-loader`}
        data-testid={LOADING_TEST_ID}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 3,
          minHeight: 120,
        }}
      >
        <LoaderComponent {...loaderProps} />
      </Box>
    )
  }

  // Fallback to default CircularProgress loader
  return (
    <Box
      className="hapana-loading default-loader"
      data-testid={LOADING_TEST_ID}
      sx={{
        ...styles.loadingContainer,
        minHeight: 120,
        padding: 3,
      }}
    >
      <CircularProgress
        size={loaderProps.size}
        sx={{
          color: '#4A90E2',
          marginBottom: 2,
        }}
      />
      {loaderProps.label && (
        <Typography
          variant="body2"
          sx={{
            ...styles.loadingText,
            textAlign: 'center',
            maxWidth: 300,
          }}
        >
          {loaderProps.label}
        </Typography>
      )}
    </Box>
  )
}

export default Loading
